import '../../../../components/content/ContentTitle.css';
import React from 'react';
import { useMusicGenreCatalogRevision } from '../../../../features/taste/generated/localization';
import { useDetailViewState } from '../detail/DetailReturnState';
import { FiEdit } from 'react-icons/fi';
import ArtistLink from '../../../../components/common/ArtistLink';
import Chevron from '../../../../components/content/Chevron';
import { useWebLocale } from '../../../../contexts/WebLocaleContext';
import { albumMetadata, albumRecordCount, memberReviewCount } from './albumIntroPresentation';
import './AlbumIntro.css';

// Same geometric silhouette and score alignment as build58 RatingStar.tsx.
function AlbumStar({ size }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        transform="translate(0 0.955)"
        d="M12 2L14.47 8.6L21.51 8.91L15.99 13.3L17.88 20.09L12 16.2L6.12 20.09L8.01 13.3L2.49 8.91L9.53 8.6L12 2Z"
      />
    </svg>
  );
}
export default function AlbumIntro({ album, reviews = album.reviews ?? [] }) {
  useMusicGenreCatalogRevision();
  const { locale } = useWebLocale();
  const en = locale === 'en';
  const [expanded, setExpanded] = useDetailViewState(
    `activity:${album.albumSource}:${album.sourceId}`,
    false,
  );
  const artist = album.artist;
  const artistName = artist?.name ?? (en ? 'Artist information unavailable' : '아티스트 정보 없음');
  const score = Number(album.averageRating || 0).toFixed(1);
  const stats = [
    { label: en ? 'Ratings' : '평가', count: album.reviewCount, icon: <AlbumStar size={17} /> },
    {
      label: en ? 'Review' : '리뷰',
      count: memberReviewCount(reviews),
      icon: <FiEdit size={15} aria-hidden="true" />,
    },
    {
      label: en ? 'Playlist' : '플리',
      count: album.playlistCount ?? 0,
      icon: (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M3 6h18M3 12h12M3 18h8M19 14v8M15 18h8" />
        </svg>
      ),
    },
  ];
  return (
    <div className="pp-album-intro">
      <div className="pp-album-intro-heading">
        <h1>
          {album.title}
          {album.contentRating === 'EXPLICIT' && (
            <>
              {'\u00a0'}
              <span className="pp-album-intro-explicit pp-inline-explicit" aria-label="Explicit">
                E
              </span>
            </>
          )}
        </h1>
        {artist?.slug || artist?.sourceId ? (
          <ArtistLink
            artistName={artistName}
            artistSource={artist.artistSource}
            artistSourceId={artist.sourceId}
            artistSlug={artist.slug}
            artistDbId={artist.id}
            sx={{
              fontFamily: 'Pretendard, sans-serif',
              fontSize: 15,
              lineHeight: '22px',
              fontWeight: 400,
            }}
          />
        ) : (
          <p className="pp-album-intro-artist">{artistName}</p>
        )}
        <p className="pp-album-intro-metadata">{albumMetadata(album, locale)}</p>
        {album.recordLabel && (
          <p className="pp-album-intro-label" title={album.recordLabel}>
            {album.recordLabel}
          </p>
        )}
      </div>
      <div className="pp-album-intro-reaction">
        <span
          className="pp-album-intro-score"
          aria-label={`${en ? 'Average rating' : '평균 평점'} ${score}`}
        >
          <AlbumStar size={22} />
          <span>{score}</span>
        </span>
        <button
          type="button"
          className="pp-album-intro-records"
          aria-expanded={expanded}
          aria-label={
            en
              ? `${expanded ? 'Collapse' : 'Expand'} album activity`
              : `앨범 기록 ${expanded ? '접기' : '펼치기'}`
          }
          onClick={() => setExpanded((value) => !value)}
        >
          <span className={`pp-album-intro-record-label${en ? ' is-en' : ''}`}>
            {en ? 'Activity' : '기록'}
            <Chevron direction="right" />
          </span>
          <span className="pp-album-intro-stats-reveal" aria-hidden={!expanded}>
            <span className="pp-album-intro-stats">
              {stats.map((stat) => (
                <span
                  key={stat.label}
                  aria-label={`${stat.label} ${albumRecordCount(stat.count, locale)}`}
                  title={stat.label}
                >
                  {stat.icon}
                  <span>{albumRecordCount(stat.count, locale)}</span>
                </span>
              ))}
            </span>
          </span>
        </button>
      </div>
    </div>
  );
}
